/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * API Client Module
 * 
 * Centralized HTTP client that conditionally routes requests to the external backend
 * or falls through to localStorage-based services depending on BACKEND_CONFIG.USE_EXTERNAL_BACKEND.
 * 
 * Usage: 
 *   import { apiClient, isExternalBackend } from '@shared/services/apiClient';
 *   
 *   if (isExternalBackend()) {
 *     const data = await apiClient.get('/api/users/123/profile');
 *   }
 */

import { BACKEND_CONFIG } from '../config';
import { Logger } from './logger';

/** Returns true when the app is configured to use the external backend API */
export const isExternalBackend = (): boolean => {
    return BACKEND_CONFIG.USE_EXTERNAL_BACKEND;
};

/** Returns the configured backend base URL */
export const getBackendUrl = (): string => {
    return BACKEND_CONFIG.BACKEND_URL;
};

interface ApiClientOptions {
    /** Override default timeout (ms). Default: 10000 */
    timeout?: number;
    /** Custom headers to merge with defaults */
    headers?: Record<string, string>;
}

/**
 * Centralized API client for backend communication.
 * All methods throw on non-ok responses after logging the error.
 */
export const apiClient = {
    async get<T = any>(path: string, options?: ApiClientOptions): Promise<T> {
        const url = `${BACKEND_CONFIG.BACKEND_URL}${path}`;
        Logger.info(`[API] GET ${path}`);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), options?.timeout || 10000);

            const token = localStorage.getItem('auth_token');
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                ...options?.headers,
            };

            const response = await fetch(url, {
                method: 'GET',
                headers,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`API GET ${path} failed: ${response.status} ${response.statusText} - ${errorBody}`);
            }

            return response.json();
        } catch (error) {
            Logger.error(`[API] GET ${path} error`, error);
            throw error;
        }
    },

    async post<T = any>(path: string, body?: any, options?: ApiClientOptions): Promise<T> {
        const url = `${BACKEND_CONFIG.BACKEND_URL}${path}`;
        Logger.info(`[API] POST ${path}`);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), options?.timeout || 10000);

            const token = localStorage.getItem('auth_token');
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                ...options?.headers,
            };

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: body !== undefined ? JSON.stringify(body) : undefined,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`API POST ${path} failed: ${response.status} ${response.statusText} - ${errorBody}`);
            }

            return response.json();
        } catch (error) {
            Logger.error(`[API] POST ${path} error`, error);
            throw error;
        }
    },

    async patch<T = any>(path: string, body?: any, options?: ApiClientOptions): Promise<T> {
        const url = `${BACKEND_CONFIG.BACKEND_URL}${path}`;
        Logger.info(`[API] PATCH ${path}`);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), options?.timeout || 10000);

            const token = localStorage.getItem('auth_token');
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                ...options?.headers,
            };

            const response = await fetch(url, {
                method: 'PATCH',
                headers,
                body: body !== undefined ? JSON.stringify(body) : undefined,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`API PATCH ${path} failed: ${response.status} ${response.statusText} - ${errorBody}`);
            }

            return response.json();
        } catch (error) {
            Logger.error(`[API] PATCH ${path} error`, error);
            throw error;
        }
    },

    async delete<T = any>(path: string, options?: ApiClientOptions): Promise<T> {
        const url = `${BACKEND_CONFIG.BACKEND_URL}${path}`;
        Logger.info(`[API] DELETE ${path}`);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), options?.timeout || 10000);

            const token = localStorage.getItem('auth_token');
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                ...options?.headers,
            };

            const response = await fetch(url, {
                method: 'DELETE',
                headers,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`API DELETE ${path} failed: ${response.status} ${response.statusText} - ${errorBody}`);
            }

            return response.json();
        } catch (error) {
            Logger.error(`[API] DELETE ${path} error`, error);
            throw error;
        }
    },
};
